import React, { useEffect, useState, useCallback } from "react";
import { useNavigate } from "react-router-dom";
import { AreaChart, Area, BarChart, Bar, XAxis, YAxis, Tooltip, ResponsiveContainer, Cell } from "recharts";
import { client } from "@/lib/api";
import { useAuth } from "@/context/AuthContext";
import { Button } from "@/components/ui/button";
import { Sparkles, TrendingUp, Award, Lock, Clock, ArrowRight } from "lucide-react";

const LONG_TERM = [
  { when: "3 months", desc: "Circulation and lung function keep climbing; coughing and wheezing ease." },
  { when: "9 months", desc: "Lungs clear out more each week — energy and breathing feel noticeably better." },
  { when: "1 year", desc: "Risk of heart disease drops to about half that of someone still using." },
  { when: "5 years", desc: "Stroke risk can fall toward that of a person who never used nicotine." },
  { when: "10 years", desc: "Risk of lung and several other cancers drops substantially." },
  { when: "15 years", desc: "Risk of heart disease approaches that of a lifelong non-user." },
];

function LockedPreview({ onUpgrade }) {
  return (
    <div className="rounded-3xl bg-card border border-border p-7 shadow-soft relative overflow-hidden" data-testid="member-insights-locked">
      <div className="absolute inset-0 pointer-events-none opacity-30 select-none" aria-hidden>
        <div className="flex items-end gap-2 h-full px-8 pt-16">
          {[40, 55, 35, 60, 48, 70, 52, 65, 44, 72, 58, 80].map((h, i) => (
            <div key={i} className="flex-1 rounded-t-lg bg-brand-teal/40" style={{ height: `${h}%` }} />
          ))}
        </div>
      </div>
      <div className="relative">
        <div className="flex items-center gap-2 mb-2">
          <div className="rounded-xl bg-sage p-2"><Sparkles className="w-5 h-5 text-brand-teal-dark" /></div>
          <span className="uppercase tracking-[0.18em] text-[11px] text-terracotta">Member insight</span>
        </div>
        <h2 className="font-serif text-2xl text-olive mb-2">See the deeper pattern</h2>
        <p className="text-olive/70 max-w-lg mb-5">
          Members see 30- and 90-day trends, when and why cravings tend to hit, and healing milestones that stretch
          years ahead. Your recovery is always free — this is the deeper view, for when you're ready.
        </p>
        <Button data-testid="insights-upgrade-btn" onClick={onUpgrade}
          className="rounded-full bg-terracotta hover:bg-terracotta-soft text-white gap-2 min-h-[48px] px-7">
          <Lock className="w-4 h-4" /> Continue your journey
        </Button>
      </div>
    </div>
  );
}

export default function MemberInsights({ planId }) {
  const navigate = useNavigate();
  const { user } = useAuth();
  const isMember = !!user?.membership?.is_member;
  const [range, setRange] = useState(30);
  const [trends, setTrends] = useState(null);
  const [cravings, setCravings] = useState(null);

  const load = useCallback(async (days) => {
    try {
      const q = planId ? `&plan_id=${planId}` : "";
      const [t, c] = await Promise.all([
        client.get(`/insights/trends?days=${days}${q}`),
        client.get(`/insights/cravings`),
      ]);
      setTrends(t.data);
      setCravings(c.data);
    } catch (e) {
      // leave as-is; section simply won't render data
    }
  }, [planId]);

  useEffect(() => { if (isMember) load(range); }, [isMember, range, load]);

  if (!isMember) return <LockedPreview onUpgrade={() => navigate("/app/membership")} />;

  const history = trends?.history || [];
  const showEvery = range >= 90 ? 15 : 5;
  const chartData = history.map((h, i) => ({ ...h, tick: i % showEvery === 0 ? h.label : "" }));
  const intensity = cravings?.intensity || {};
  const trend = intensity.recent_avg != null && intensity.early_avg != null
    ? (intensity.recent_avg < intensity.early_avg ? "easing" : intensity.recent_avg > intensity.early_avg ? "climbing" : "steady")
    : null;
  const topMax = Math.max(1, ...(cravings?.top_triggers || []).map((t) => t.count));

  return (
    <div className="space-y-6" data-testid="member-insights">
      <div className="flex items-center gap-2">
        <div className="rounded-xl bg-sage p-2"><Sparkles className="w-5 h-5 text-brand-teal-dark" /></div>
        <span className="uppercase tracking-[0.18em] text-[11px] text-terracotta">Member insights</span>
      </div>

      {/* Long-term trend */}
      <div className="rounded-3xl bg-card border border-border p-7 shadow-soft" data-testid="insights-trends-card">
        <div className="flex flex-wrap items-center justify-between gap-3 mb-1">
          <h2 className="font-serif text-2xl text-olive flex items-center gap-2"><TrendingUp className="w-5 h-5 text-sky" /> The longer arc</h2>
          <div className="flex gap-1.5">
            {[30, 90].map((d) => (
              <button key={d} data-testid={`trend-range-${d}`} onClick={() => setRange(d)}
                className={`rounded-full px-3.5 py-1.5 text-xs font-medium border transition-colors ${range === d ? "bg-brand-teal text-brand-navy border-brand-teal" : "bg-card text-olive/70 border-border hover:bg-sage"}`}>
                {d} days
              </button>
            ))}
          </div>
        </div>
        {trends?.summary && (
          <p className="text-sm text-stone mb-4" data-testid="insights-summary">
            <span className="text-brand-teal-dark font-medium">{trends.summary.days_on_track}</span> of {trends.summary.total_days} days on track over this window.
          </p>
        )}
        <div className="h-56 w-full">
          <ResponsiveContainer width="100%" height="100%">
            <AreaChart data={chartData} margin={{ top: 8, right: 8, left: -18, bottom: 0 }}>
              <defs>
                <linearGradient id="cnt" x1="0" y1="0" x2="0" y2="1">
                  <stop offset="0%" stopColor="#00D1C2" stopOpacity={0.5} />
                  <stop offset="100%" stopColor="#00D1C2" stopOpacity={0.05} />
                </linearGradient>
              </defs>
              <XAxis dataKey="tick" tick={{ fill: "#5B6472", fontSize: 11 }} axisLine={false} tickLine={false} interval={0} />
              <YAxis allowDecimals={false} tick={{ fill: "#5B6472", fontSize: 11 }} axisLine={false} tickLine={false} />
              <Tooltip
                labelFormatter={(_, p) => p?.[0]?.payload?.label || ""}
                contentStyle={{ background: "#FFFFFF", border: "1px solid #E4E9ED", borderRadius: 12, fontFamily: "Outfit", color: "#14181D" }}
                formatter={(v, n) => [v, n === "target" ? "Goal" : "You had"]}
              />
              <Area type="monotone" dataKey="target" name="target" stroke="#C9D2DA" fill="none" strokeDasharray="4 4" strokeWidth={1.5} />
              <Area type="monotone" dataKey="count" name="count" stroke="#0C8478" strokeWidth={2} fill="url(#cnt)" />
            </AreaChart>
          </ResponsiveContainer>
        </div>
      </div>

      <div className="grid lg:grid-cols-2 gap-6">
        {/* Craving patterns */}
        <div className="rounded-3xl bg-card border border-border p-7 shadow-soft" data-testid="insights-cravings-card">
          <h2 className="font-serif text-2xl text-olive mb-4 flex items-center gap-2"><Clock className="w-5 h-5 text-sky" /> Your craving patterns</h2>
          {(cravings?.total || 0) === 0 ? (
            <p className="text-sm text-olive/70">Log a few cravings in My Journey and your patterns will start to appear here.</p>
          ) : (
            <>
              <p className="text-[11px] uppercase tracking-[0.15em] text-stone mb-2">Top triggers</p>
              <div className="space-y-2 mb-5" data-testid="insights-top-triggers">
                {(cravings.top_triggers || []).map((t) => (
                  <div key={t.trigger} className="flex items-center gap-3">
                    <span className="text-sm text-olive w-28 shrink-0 truncate capitalize">{t.trigger}</span>
                    <div className="flex-1 h-2.5 rounded-full bg-sage overflow-hidden">
                      <div className="h-full rounded-full bg-brand-teal" style={{ width: `${(t.count / topMax) * 100}%` }} />
                    </div>
                    <span className="text-xs text-stone w-6 text-right">{t.count}</span>
                  </div>
                ))}
              </div>
              <p className="text-[11px] uppercase tracking-[0.15em] text-stone mb-2">When they hit</p>
              <div className="h-28 w-full mb-4">
                <ResponsiveContainer width="100%" height="100%">
                  <BarChart data={cravings.time_of_day || []} margin={{ top: 4, right: 8, left: -22, bottom: 0 }}>
                    <XAxis dataKey="label" tick={{ fill: "#5B6472", fontSize: 11 }} axisLine={false} tickLine={false} />
                    <YAxis allowDecimals={false} tick={{ fill: "#5B6472", fontSize: 11 }} axisLine={false} tickLine={false} />
                    <Bar dataKey="count" radius={[6, 6, 0, 0]}>
                      {(cravings.time_of_day || []).map((_, i) => <Cell key={i} fill="#0C8478" />)}
                    </Bar>
                  </BarChart>
                </ResponsiveContainer>
              </div>
              {trend && (
                <p className="text-sm text-olive/80" data-testid="insights-intensity">
                  Average intensity is <span className={`font-medium ${trend === "easing" ? "text-brand-teal-dark" : "text-terracotta"}`}>{trend}</span> —
                  from {intensity.early_avg} to {intensity.recent_avg} out of 5.
                </p>
              )}
            </>
          )}
        </div>

        {/* Longer-term milestones */}
        <div className="rounded-3xl bg-card border border-border p-7 shadow-soft" data-testid="insights-milestones-card">
          <h2 className="font-serif text-2xl text-olive mb-4 flex items-center gap-2"><Award className="w-5 h-5 text-gold" /> Milestones ahead</h2>
          <ul className="space-y-3.5">
            {LONG_TERM.map((m) => (
              <li key={m.when} className="flex gap-3">
                <span className="font-serif text-base text-brand-teal-dark w-20 shrink-0">{m.when}</span>
                <span className="text-sm text-olive/75">{m.desc}</span>
              </li>
            ))}
          </ul>
        </div>
      </div>
    </div>
  );
}
